import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
    Box,
    Paper,
    Chip,
    CircularProgress,
    Snackbar,
    Alert,
    IconButton,
    Tooltip,
} from "@mui/material";
import { Refresh, CloudDone, CloudOff, Dns } from "@mui/icons-material";
import CommonTable from "../../../common/CommonTable";
import "./SystemMonitoring.css";

const backendUrl = (process.env.REACT_APP_BACKEND_API_URL_LOCAL || "").replace(/\/$/, "");
const API_URL = `${backendUrl}/common/system-status/`;

const SystemMonitoring = () => {
    const [results, setResults] = useState([]);
    const [summary, setSummary] = useState({ total: 0, up: 0, down: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const fetchStatus = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(API_URL);
            const data = await response.json();
            if (!response.ok) throw new Error(data?.message || "Failed to fetch system status");

            setResults(Array.isArray(data.results) ? data.results : []);
            setSummary({
                total: data.total ?? 0,
                up: data.up ?? 0,
                down: data.down ?? 0,
            });
        } catch (err) {
            console.error("Error fetching system status:", err);
            setError(err.message || "Failed to fetch system status");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStatus();
    }, [fetchStatus]);

    const handleCopyUrl = useCallback((row) => {
        const fullUrl = `${backendUrl}${row.url}`;
        navigator.clipboard
            .writeText(fullUrl)
            .then(() =>
                setSnackbar({ open: true, message: "URL copied to clipboard", severity: "success" })
            )
            .catch(() =>
                setSnackbar({ open: true, message: "Failed to copy URL", severity: "error" })
            );
    }, []);

    const columns = useMemo(
        () => [
            { key: "SlNo", label: "S.No", sortable: false, align: "center" },
            { key: "name", label: "API Name", sortable: true, align: "left" },
            { key: "module_name", label: "Module", sortable: true, align: "left" },
            {
                key: "url",
                label: "URL",
                sortable: false,
                align: "left",
                render: (row) => (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 260 }}>
                        <Box
                            sx={{
                                flex: 1,
                                border: "1px solid #cbd5e1",
                                borderRadius: "6px",
                                padding: "4px 8px",
                                fontSize: "12.5px",
                                color: "#334155",
                                background: "#fff",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: 360,
                            }}
                            title={row.url}
                        >
                            {row.url}
                        </Box>
                        <button
                            onClick={() => handleCopyUrl(row)}
                            style={{
                                padding: "5px 12px",
                                background: "#f4c430",
                                color: "#1e293b",
                                border: "none",
                                borderRadius: "6px",
                                fontSize: "12.5px",
                                fontWeight: 700,
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                            }}
                        >
                            Copy
                        </button>
                    </Box>
                ),
            },
            {
                key: "response_time_ms",
                label: "Response Time (ms)",
                sortable: true,
                align: "center",
            },
            {
                key: "status",
                label: "Service",
                sortable: true,
                align: "center",
                render: (row) => (
                    <span
                        style={{
                            display: "inline-block",
                            minWidth: "70px",
                            padding: "5px 10px",
                            borderRadius: "6px",
                            fontWeight: 700,
                            fontSize: "12.5px",
                            color: "#fff",
                            backgroundColor: row.status === "UP" ? "#1f6b3d" : "#8b3a3a",
                        }}
                    >
                        {row.status === "UP" ? "Success" : "Fail"}
                    </span>
                ),
            },
            {
                key: "remarks",
                label: "Remarks",
                sortable: false,
                align: "left",
                render: (row) => row.last_error || `HTTP ${row.status_code ?? "-"}`,
            },
        ],
        [handleCopyUrl]
    );

    return (
        <div className="system-monitoring-container">
            <Paper elevation={0} sx={{ borderRadius: "16px", border: "1px solid #e2e8f0", overflow: "hidden" }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1.5,
                        borderBottom: "1px solid #e2e8f0",
                        px: 2,
                    }}
                >
                    <Box sx={{ fontWeight: 700, fontSize: "1rem", color: "#1e293b", py: 1.5 }}>
                        
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                        <Chip
                            icon={<Dns fontSize="small" />}
                            label={`Total: ${summary.total}`}
                            sx={{ fontWeight: 700, backgroundColor: "#eef2ff", color: "#3730a3" }}
                        />
                        <Chip
                            icon={<CloudDone fontSize="small" />}
                            label={`Up: ${summary.up}`}
                            sx={{ fontWeight: 700, backgroundColor: "#dcfce7", color: "#15803d" }}
                        />
                        <Chip
                            icon={<CloudOff fontSize="small" />}
                            label={`Down: ${summary.down}`}
                            sx={{ fontWeight: 700, backgroundColor: "#fee2e2", color: "#b91c1c" }}
                        />
                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchStatus} disabled={loading}>
                                <Refresh />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "300px" }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Box sx={{ p: 4 }}>
                        <Alert severity="error">{error}</Alert>
                    </Box>
                ) : (
                    <CommonTable
                        tableData={results}
                        columns={columns}
                        tableName="Internal_APIs"
                        searchableKeys={["name", "module_name", "url"]}
                        searchPlaceholder="Search by API name, module, or URL..."
                        emptyMessage="No APIs found."
                    />
                )}
            </Paper>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert severity={snackbar.severity} variant="filled" sx={{ minWidth: "260px" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default SystemMonitoring;
